import { useUIStore } from "@/stores";
import { formatEmail } from "@/utils";
import { clipboard, REGEX, VALIDATE } from "@/utils";
import { ContentCopy, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import { FC, FormEvent, useState } from "react";
import { useNotify } from "react-admin";
import { z } from "zod";
import { useCreateAdminUser } from "../../queries";
import { CreateAdminUserProps } from "./create-admin-user.types";

const initialFormState = {
  fullName: "",
  email: "",
  phoneNumber: "",
  password: "",
};

const formSchema = z.object({
  fullName: z
    .string()
    .min(1, "Field is required")
    .max(50, "Field must be at most 50 characters"),
  email: z.string().min(1, "Field is required").email("Wrong format"),
  phoneNumber: z
    .string()
    .min(1, "Field is required")
    .refine((phoneNumber) => VALIDATE.phoneNumber(phoneNumber), "Wrong format"),
  password: z
    .string()
    .min(1, "Field is required")
    .max(20, "Field must be at most 20 characters"),
});

type FormData = z.infer<typeof formSchema>;

const CreateAdminUser: FC<CreateAdminUserProps> = ({ role }) => {
  const notify = useNotify();
  const { closeModal } = useUIStore();

  const { createAdminUser, createAdminUserLoading } = useCreateAdminUser();

  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({
    fullName: false,
    email: false,
    phoneNumber: false,
    password: false,
  });
  const [adminUserFormData, setAdminUserFormData] = useState<Partial<FormData>>(
    {},
  );

  const formData: FormData = {
    ...initialFormState,
    ...adminUserFormData,
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    createAdminUser({ ...formData, role });
  };
  const validate = () => {
    const res = formSchema.safeParse(formData);
    if (res.success) {
      return undefined;
    }
    return res.error.format();
  };

  const errors = validate();
  const isCopyButtonDisabled = !formData.email || !formData.password;
  const isFormFilled = Object.values(formData).every((v) => !!v);

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      sx={{
        display: "flex",
        flexDirection: "column",
        rowGap: 0.5,
      }}
    >
      <Divider sx={{ borderColor: "#333" }} />

      <Stack mt={1} mb={2} gap={1}>
        <TextField
          required
          fullWidth
          variant="outlined"
          placeholder="Full name"
          label={
            touched.fullName && errors?.fullName
              ? errors?.fullName?._errors[0]
              : "Full Name"
          }
          onBlur={() => setTouched((prev) => ({ ...prev, fullName: true }))}
          error={touched.fullName ? !!errors?.fullName?._errors.length : false}
          value={formData.fullName}
          onChange={(e) => {
            setAdminUserFormData((prev) => ({
              ...prev,
              fullName: e.target.value,
            }));
            if (touched.fullName) {
              validate();
            }
          }}
        />
        <TextField
          required
          fullWidth
          variant="outlined"
          placeholder="Email address"
          label={
            touched.email && errors?.email
              ? errors?.email?._errors[0]
              : "Email Address"
          }
          onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
          error={touched.email ? !!errors?.email?._errors.length : false}
          value={formData.email}
          slotProps={{
            htmlInput: {
              autoCapitalize: "none",
            },
          }}
          onChange={(e) => {
            setAdminUserFormData((prev) => ({
              ...prev,
              email: formatEmail(e.target.value),
            }));
            if (touched.email) {
              validate();
            }
          }}
        />
        <TextField
          required
          fullWidth
          variant="outlined"
          placeholder="Phone number"
          label={
            touched.phoneNumber && errors?.phoneNumber
              ? errors?.phoneNumber?._errors[0]
              : "Phone Number"
          }
          onBlur={() => setTouched((prev) => ({ ...prev, phoneNumber: true }))}
          error={
            touched.phoneNumber ? !!errors?.phoneNumber?._errors.length : false
          }
          value={formData.phoneNumber}
          onChange={(e) => {
            let value = e.target.value;

            if (value && !value.startsWith("+")) {
              value = "+" + value;
            }
            if (!REGEX.PLUS_NUMBERS.test(value)) return;

            setAdminUserFormData((prev) => ({
              ...prev,
              phoneNumber: value,
            }));
            if (touched.phoneNumber) {
              validate();
            }
          }}
        />
        <TextField
          required
          fullWidth
          variant="outlined"
          type={showPassword ? "text" : "password"}
          placeholder="password"
          label={
            touched.password && errors?.password
              ? errors?.password?._errors[0]
              : "Password"
          }
          onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
          error={touched.password ? !!errors?.password?._errors.length : false}
          value={formData.password}
          onChange={(e) => {
            setAdminUserFormData((prev) => ({
              ...prev,
              password: e.target.value,
            }));
            if (touched.password) {
              validate();
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <IconButton
          disabled={isCopyButtonDisabled}
          sx={{
            border: `1px solid ${isCopyButtonDisabled ? "#333" : "white"}`,
            borderRadius: 1,
          }}
          onClick={() =>
            clipboard(`email:${formData.email};password:${formData.password}`)
              .then(() => notify("Credentials copied!", { type: "success" }))
              .catch((error) => {
                console.error(error.message);
                notify("Credentials are not copied!");
              })
          }
        >
          <ContentCopy fontSize="small" />
        </IconButton>

        <Stack direction={"row"} gap={1}>
          <Button variant="outlined" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            loading={createAdminUserLoading}
            disabled={createAdminUserLoading || !isFormFilled || !!errors}
          >
            Create
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export { CreateAdminUser };
